const MAX_STRING_LENGTH = 512;
const MAX_ARRAY_ITEMS = 20;
const MAX_DEPTH = 5;

const SENSITIVE_KEY_PATTERN =
  /(?:authorization|cookie|credential|csrf|email|identity|jwt|openid|password|phone|secret|session|token|unionid)/i;

type JsonRecord = Record<string, unknown>;

type SentryEventLike = {
  request?: {
    data?: unknown;
    query_string?: unknown;
    headers?: unknown;
    cookies?: unknown;
    env?: unknown;
  };
  extra?: unknown;
  user?: JsonRecord;
};

type SentryBreadcrumbLike = {
  category?: string;
  data?: unknown;
  message?: string;
};

type SentrySamplingContextLike = {
  attributes?: JsonRecord;
  inheritOrSampleWith?: (fallbackSampleRate: number) => number;
  location?: { pathname?: string };
  name?: string;
  normalizedRequest?: {
    method?: string;
    url?: string;
  };
  parentSampled?: boolean;
};

export const sentryTracePropagationTargets: Array<string | RegExp> = [
  /^\/api(?:\/|$)/,
  /^https?:\/\/(?:localhost|127\.0\.0\.1):3000(?:\/|$)/,
];

export function sanitizeForSentry(value: unknown, depth = 0): unknown {
  if (value == null) return value;

  if (typeof value === "string") {
    return truncateString(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof URL) {
    return `${value.origin}${value.pathname}`;
  }

  if (depth >= MAX_DEPTH) {
    return "[max depth]";
  }

  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_ITEMS).map((item) => sanitizeForSentry(item, depth + 1));
  }

  if (!isRecord(value)) {
    return String(value);
  }

  const sanitized: JsonRecord = {};
  for (const [key, entry] of Object.entries(value)) {
    sanitized[key] = isSensitiveKey(key) ? "[redacted]" : sanitizeForSentry(entry, depth + 1);
  }
  return sanitized;
}

export function scrubSentryEvent<T extends SentryEventLike>(event: T): T {
  if (event.request) {
    event.request.data = sanitizeForSentry(event.request.data);
    event.request.query_string = sanitizeForSentry(event.request.query_string);
    event.request.headers = sanitizeForSentry(event.request.headers);
    event.request.cookies = event.request.cookies ? "[redacted]" : event.request.cookies;
    event.request.env = sanitizeForSentry(event.request.env);
  }

  if (event.extra) {
    event.extra = sanitizeForSentry(event.extra);
  }

  if (event.user) {
    const { id, username } = event.user;
    event.user = { id, username };
  }

  return event;
}

export function scrubSentryBreadcrumb<T extends SentryBreadcrumbLike>(breadcrumb: T): T | null {
  if (breadcrumb.category === "ui.click") {
    return null;
  }

  if (breadcrumb.data) {
    breadcrumb.data = sanitizeForSentry(breadcrumb.data);
  }
  if (breadcrumb.message) {
    breadcrumb.message = truncateString(breadcrumb.message);
  }

  return breadcrumb;
}

export function sentryTracesSampler(context: SentrySamplingContextLike): number {
  const name = context.name ?? "";
  const path = getSamplingPath(context);
  const target = `${name} ${path}`;

  if (isNoisyTransaction(target)) {
    return 0;
  }

  if (isImportantTransaction(target)) {
    return inheritOrSampleWith(context, 0.5);
  }

  return inheritOrSampleWith(context, 0.1);
}

function inheritOrSampleWith(context: SentrySamplingContextLike, fallback: number): number {
  return context.inheritOrSampleWith?.(fallback) ?? fallback;
}

function getSamplingPath(context: SentrySamplingContextLike): string {
  const attrTarget =
    readStringAttribute(context.attributes, "http.target") ??
    readStringAttribute(context.attributes, "url.path") ??
    readStringAttribute(context.attributes, "http.route");
  if (attrTarget) return attrTarget;

  if (context.location?.pathname) {
    return context.location.pathname;
  }

  const url = context.normalizedRequest?.url;
  if (!url) return "";

  try {
    return new URL(url, "http://localhost").pathname;
  } catch {
    return url;
  }
}

function readStringAttribute(attributes: JsonRecord | undefined, key: string): string | undefined {
  const value = attributes?.[key];
  return typeof value === "string" ? value : undefined;
}

function isNoisyTransaction(value: string): boolean {
  return /(?:\/api\/health|\/robots\.txt|\/sitemap\.xml|\/favicon\.ico|\/assets\/)/.test(value);
}

function isImportantTransaction(value: string): boolean {
  return /(?:\/admin|\/api\/admin|\/api\/auth|\/api\/crawler|\/api\/reviews|\/api\/sync|\/api\/wechat)/.test(
    value,
  );
}

function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERN.test(key);
}

function truncateString(value: string): string {
  return value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}...[truncated]`
    : value;
}

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === "object" && value !== null;
}
